import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Briefcase, LayoutList, ScrollText, Building2, FileText, ChevronRight, Mail, Phone, Linkedin, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { resolveImage } from "../components/admin/ImageUpload";

/**
 * Corporate Governance page — board of directors, executive team, committees.
 * Editorial photo-grid style inspired by BA Holding / Polat Holding.
 * Content is managed via /admin/team.
 */

const NAV = [
  { key: "board", icon: Users, label: { tr: "Yönetim Kurulu", en: "Board of Directors", ar: "مجلس الإدارة" } },
  { key: "executive", icon: Briefcase, label: { tr: "İcra Kurulu", en: "Executive Committee", ar: "اللجنة التنفيذية" } },
  { key: "committees", icon: LayoutList, label: { tr: "Komiteler", en: "Committees", ar: "اللجان" } },
  { key: "org", icon: Building2, label: { tr: "Organizasyon Şeması", en: "Organization Chart", ar: "الهيكل التنظيمي" } },
  { key: "charter", icon: ScrollText, label: { tr: "Esas Sözleşme", en: "Articles of Association", ar: "النظام الأساسي" } },
  { key: "policies", icon: FileText, label: { tr: "Politikalar & Etik", en: "Policies & Ethics", ar: "السياسات والأخلاق" } },
];

const GovernancePage = () => {
  const { pick, lang } = useI18n();
  const [team, setTeam] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openMember, setOpenMember] = useState(null);

  useEffect(() => {
    api
      .get("/public/team")
      .then((r) => setTeam(r.data || []))
      .catch(() => setTeam([]))
      .finally(() => setLoaded(true));
  }, []);

  const leaders = team.filter((t) => t.is_leader);
  const others = team.filter((t) => !t.is_leader);

  return (
    <div data-testid="governance-page" className="bg-white">
      {/* Compact header — no big banner */}
      <section className="pt-32 md:pt-36 pb-4 md:pb-6 bg-white">
        <div className="container-x">
          <nav className="flex items-center gap-2 text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-primary transition-colors">
              {pick({ tr: "Ana Sayfa", en: "Home", ar: "الرئيسية" })}
            </Link>
            <ChevronRight size={12} strokeWidth={1.5} />
            <Link to="/about" className="hover:text-brand-primary transition-colors">
              {pick({ tr: "Hakkımızda", en: "About", ar: "من نحن" })}
            </Link>
            <ChevronRight size={12} strokeWidth={1.5} />
            <span className="text-brand-navy">{pick({ tr: "Kurumsal Yönetim", en: "Corporate Governance", ar: "الحوكمة المؤسسية" })}</span>
          </nav>
        </div>
      </section>

      {/* Hero header */}
      <section className="bg-white pb-6 md:pb-10">
        <div className="container-x">
          <div className="flex items-center gap-4 mb-5">
            <span className="h-px w-12 bg-brand-primary" />
            <span className="text-[0.7rem] tracking-[0.36em] uppercase text-brand-primary font-medium">
              {pick({ tr: "Kurumsal Yönetim", en: "Corporate Governance", ar: "الحوكمة المؤسسية" })}
            </span>
          </div>
          <h1
            className="font-serif text-brand-navy leading-[1.02] tracking-tight max-w-4xl"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
          >
            {pick({
              tr: "Şeffaflık ile inşa edilen liderlik.",
              en: "Leadership built on transparency.",
              ar: "قيادة مبنية على الشفافية.",
            })}
          </h1>
          <p className="mt-5 text-brand-medium max-w-2xl leading-relaxed">
            {pick({
              tr: "Fırat Construction Yönetim Kurulu, uluslararası tecrübeyle sektörel derinliği bir araya getirerek her yatırımda paydaşlarımızın güvenini korur.",
              en: "The Fırat Construction Board combines international experience with sector depth to safeguard stakeholder trust across every investment.",
              ar: "يجمع مجلس إدارة فرات بين الخبرة الدولية والعمق القطاعي لحماية ثقة المساهمين.",
            })}
          </p>
        </div>
      </section>

      {/* Two-column layout: sticky sidebar + board grid */}
      <section className="bg-white pb-16 md:pb-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left sidebar — governance topics */}
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-32">
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-medium mb-5">
                {pick({ tr: "Bölümler", en: "Sections", ar: "الأقسام" })}
              </div>
              <nav className="space-y-1">
                {NAV.map((n, i) => {
                  const Icon = n.icon;
                  const on = i === 0;
                  return (
                    <button
                      type="button"
                      key={n.key}
                      className={`w-full flex items-center gap-3 px-4 py-3 border-l-2 text-sm transition-all text-left ${
                        on
                          ? "border-brand-primary bg-brand-light text-brand-navy font-medium"
                          : "border-transparent text-brand-medium hover:border-brand-primary/40 hover:text-brand-navy hover:bg-brand-light/60"
                      }`}
                      data-testid={`governance-nav-${n.key}`}
                    >
                      <Icon size={14} strokeWidth={1.5} />
                      <span>{pick(n.label)}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Board grid */}
          <div className="lg:col-span-9">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-8 bg-brand-primary" />
              <h2 className="font-serif text-brand-navy text-2xl md:text-3xl">
                {pick({ tr: "Yönetim Kurulu", en: "Board of Directors", ar: "مجلس الإدارة" })}
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {!loaded
                ? Array.from({ length: 8 }).map((_, i) => (
                    <div key={`sk-${i}`} data-testid={`board-skeleton-${i}`}>
                      <div className="aspect-[4/5] bg-black/5 animate-pulse" />
                      <div className="mt-4 h-4 w-3/4 bg-black/5 animate-pulse rounded-sm" />
                      <div className="mt-2 h-3 w-1/2 bg-black/5 animate-pulse rounded-sm" />
                    </div>
                  ))
                : (leaders.length ? leaders : team).map((m, i) => {
                const displayName = m.name && typeof m.name === "object" ? pick(m.name) : m.name;
                const displayRole = m.title ? pick(m.title) : pick(m.role);
                const photo = m.photo ? resolveImage(m.photo) : m.portrait;
                const videoUrl = m.hover_video ? resolveImage(m.hover_video) : "";
                const videoPoster = m.hover_video_poster ? resolveImage(m.hover_video_poster) : photo;
                const hasBio = !!(m.bio && (m.bio.tr || m.bio.en || m.bio.ar));
                return (
                  <motion.button
                    type="button"
                    onClick={() => hasBio && setOpenMember(m)}
                    key={m.slug || m.name || i}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, delay: i * 0.05, ease: [0.19, 1, 0.22, 1] }}
                    className={`group text-left focus:outline-none ${hasBio ? "cursor-pointer" : ""}`}
                    data-testid={`board-member-${m.slug || i}`}
                  >
                    <div
                      className="relative aspect-[4/5] overflow-hidden bg-brand-navy"
                      onMouseEnter={(e) => { const v = e.currentTarget.querySelector("video"); if (v) { v.currentTime = 0; v.play().catch(() => {}); } }}
                      onMouseLeave={(e) => { const v = e.currentTarget.querySelector("video"); if (v) { v.pause(); } }}
                    >
                      {photo ? (
                        <img
                          src={photo}
                          alt={displayName}
                          loading="lazy"
                          className={`absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1200ms] group-hover:scale-105 ${videoUrl ? "group-hover:opacity-0" : ""}`}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-white/40 font-serif text-6xl">
                          {(displayName || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                      )}
                      {videoUrl ? (
                        <video
                          src={videoUrl}
                          poster={videoPoster || undefined}
                          muted
                          loop
                          playsInline
                          preload="none"
                          className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          aria-hidden="true"
                        />
                      ) : null}
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/40 via-transparent to-transparent pointer-events-none" />
                      {hasBio ? (
                        <div className="absolute inset-x-0 bottom-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="inline-block bg-white/95 text-brand-navy text-[0.55rem] tracking-[0.24em] uppercase font-medium px-2 py-1">
                            Detay
                          </span>
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-4">
                      <div className="font-serif text-brand-navy text-base md:text-lg leading-tight">{displayName}</div>
                      <div className="mt-1 text-xs md:text-sm text-brand-medium leading-snug">{displayRole}</div>
                      {hasBio ? (
                        <>
                          <p className="mt-3 text-xs md:text-[0.82rem] text-brand-dark/75 leading-relaxed line-clamp-3">
                            {pick(m.bio)}
                          </p>
                          <span
                            className="mt-3 inline-flex items-center gap-1.5 text-[0.62rem] tracking-[0.28em] uppercase text-brand-primary font-medium group-hover:text-brand-navy transition-colors"
                            data-testid={`board-member-more-${m.slug || i}`}
                          >
                            Daha Fazla
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-0.5 transition-transform">
                              <line x1="5" y1="12" x2="19" y2="12"/>
                              <polyline points="12 5 19 12 12 19"/>
                            </svg>
                          </span>
                        </>
                      ) : null}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Ekibimiz — full team with bios */}
            {others.length ? (
              <div className="mt-20" data-testid="team-section">
                <div className="flex items-center gap-4 mb-8">
                  <span className="h-px w-8 bg-brand-primary" />
                  <h2 className="font-serif text-brand-navy text-2xl md:text-3xl">
                    {pick({ tr: "Ekibimiz", en: "Our Team", ar: "فريقنا" })}
                  </h2>
                </div>
                <p className="text-brand-medium max-w-2xl mb-10 leading-relaxed">
                  {pick({
                    tr: "Fırat Holding'in 30 yılı aşkın birikimini birlikte inşa eden departman liderleri.",
                    en: "The department leaders who together build Fırat Holding's 30+ years of expertise.",
                    ar: "قادة الأقسام الذين يبنون معًا خبرة فرات القابضة الممتدة لأكثر من 30 عامًا.",
                  })}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  {others.map((m, i) => {
                    const displayName = pick(m.name);
                    const displayRole = pick(m.title);
                    const displayBio  = pick(m.bio);
                    const photo = resolveImage(m.photo);
                    return (
                      <motion.button
                        type="button"
                        onClick={() => setOpenMember(m)}
                        key={m.slug || i}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.6, delay: i * 0.04 }}
                        className="flex gap-5 items-start bg-brand-light/60 border border-black/5 p-5 md:p-6 hover:border-brand-navy hover:shadow-[0_18px_40px_-24px_rgba(34,52,107,0.25)] transition-all text-left focus:outline-none cursor-pointer"
                        data-testid={`team-member-${m.slug}`}
                      >
                        <div className="relative w-24 h-24 md:w-28 md:h-28 flex-shrink-0 overflow-hidden bg-brand-navy">
                          {photo ? (
                            <img src={photo} alt={displayName} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/50 font-serif text-2xl">
                              {(displayName || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-serif text-brand-navy text-lg md:text-xl leading-tight">{displayName}</div>
                          <div className="text-xs md:text-sm text-brand-primary tracking-[0.14em] uppercase mt-1">{displayRole}</div>
                          {displayBio ? (
                            <p className="mt-3 text-sm text-brand-dark leading-relaxed">{displayBio}</p>
                          ) : null}
                          <div className="mt-3 flex items-center gap-3 text-brand-medium">
                            {m.email ? <a href={`mailto:${m.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-brand-navy" aria-label="E-posta"><Mail size={14} strokeWidth={1.5} /></a> : null}
                            {m.phone ? <a href={`tel:${m.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-brand-navy" aria-label="Telefon"><Phone size={14} strokeWidth={1.5} /></a> : null}
                            {m.linkedin_url ? <a href={m.linkedin_url} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" className="hover:text-brand-navy" aria-label="LinkedIn"><Linkedin size={14} strokeWidth={1.5} /></a> : null}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* Executive summary card */}
            <div className="mt-16 border border-brand-primary/15 bg-brand-light/60 p-6 md:p-8">
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-primary font-medium mb-3">
                {pick({ tr: "Kurumsal İlkelerimiz", en: "Our Corporate Principles", ar: "مبادئنا المؤسسية" })}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                {[
                  { tr: "Şeffaflık", en: "Transparency", ar: "الشفافية" },
                  { tr: "Hesap Verebilirlik", en: "Accountability", ar: "المساءلة" },
                  { tr: "Sorumluluk", en: "Responsibility", ar: "المسؤولية" },
                ].map((p, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center flex-shrink-0 font-serif">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-serif text-brand-navy text-lg leading-tight">{pick(p)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Member detail modal */}
      <AnimatePresence>
        {openMember ? (
          <MemberModal
            member={openMember}
            pick={pick}
            onClose={() => setOpenMember(null)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
};

/* -------------------------------------------------------
 * Member detail modal — photo left, bio + contact right
 * ------------------------------------------------------- */
/* Social icon set — SVG-based so we don't add a dependency. Each entry is a
   flat monoline icon rendered inside a circular pill button. */
const SocialIcon = ({ kind }) => {
  const svgProps = { width: 15, height: 15, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (kind) {
    case "linkedin":
      return (<svg {...svgProps}><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>);
    case "instagram":
      return (<svg {...svgProps}><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>);
    case "facebook":
      return (<svg {...svgProps}><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>);
    case "twitter":
      return (<svg {...svgProps}><path d="M22 4l-9.5 10L22 4zM22 4l-8 18-4-9-9-4 21-5z" strokeWidth="1.4"/></svg>);
    case "youtube":
      return (<svg {...svgProps}><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></svg>);
    case "tiktok":
      return (<svg {...svgProps}><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>);
    case "website":
      return (<svg {...svgProps}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>);
    case "whatsapp":
      return (<svg {...svgProps}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>);
    default:
      return null;
  }
};

const buildSocials = (member) => {
  const items = [];
  if (member.linkedin_url)  items.push({ kind: "linkedin",  label: "LinkedIn",  href: member.linkedin_url });
  if (member.instagram_url) items.push({ kind: "instagram", label: "Instagram", href: member.instagram_url });
  if (member.facebook_url)  items.push({ kind: "facebook",  label: "Facebook",  href: member.facebook_url });
  if (member.twitter_url)   items.push({ kind: "twitter",   label: "X (Twitter)", href: member.twitter_url });
  if (member.youtube_url)   items.push({ kind: "youtube",   label: "YouTube",   href: member.youtube_url });
  if (member.tiktok_url)    items.push({ kind: "tiktok",    label: "TikTok",    href: member.tiktok_url });
  if (member.website_url)   items.push({ kind: "website",   label: "Web Sitesi", href: member.website_url });
  if (member.whatsapp) {
    const clean = String(member.whatsapp).replace(/[^0-9]/g, "");
    if (clean) items.push({ kind: "whatsapp", label: "WhatsApp", href: `https://wa.me/${clean}` });
  }
  return items;
};

const MemberModal = ({ member, pick, onClose }) => {
  const displayName = member.name && typeof member.name === "object" ? pick(member.name) : member.name;
  const displayRole = member.title ? pick(member.title) : pick(member.role || {});
  const displayBio  = member.bio ? pick(member.bio) : "";
  const photo = member.photo ? resolveImage(member.photo) : member.portrait;
  const socials = buildSocials(member);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="member-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
        className="relative bg-white w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 text-brand-navy hover:bg-white flex items-center justify-center shadow-md"
          data-testid="member-modal-close"
        >
          <X size={18} strokeWidth={1.5} />
        </button>
        {/* Photo — strictly contained inside its grid column */}
        <div className="relative bg-brand-navy overflow-hidden min-w-0">
          <div className="aspect-[4/5] md:aspect-auto md:h-full w-full">
            {photo ? (
              <img src={photo} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/40 font-serif text-7xl">
                {(displayName || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
            )}
          </div>
        </div>
        {/* Info */}
        <div className="min-w-0 overflow-y-auto p-6 md:p-8 lg:p-10">
          {member.department ? (
            <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-primary font-medium mb-4 break-words">
              {member.department}
            </div>
          ) : null}
          <h2 className="font-serif text-brand-navy leading-[1.05] break-words" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.25rem)" }}>
            {displayName}
          </h2>
          <div className="mt-2 text-xs md:text-sm text-brand-primary tracking-[0.14em] uppercase font-medium break-words">
            {displayRole}
          </div>
          {displayBio ? (
            <p className="mt-6 text-brand-dark leading-relaxed whitespace-pre-line break-words">
              {displayBio}
            </p>
          ) : null}
          {/* Contact rows */}
          {(member.email || member.phone) ? (
            <div className="mt-8 pt-6 border-t border-black/10 space-y-3">
              {member.email ? (
                <a href={`mailto:${member.email}`} className="flex items-center gap-3 text-brand-dark hover:text-brand-primary break-all">
                  <span className="w-9 h-9 rounded-full bg-brand-navy/5 text-brand-navy inline-flex items-center justify-center flex-shrink-0"><Mail size={15} strokeWidth={1.5} /></span>
                  <span className="text-sm">{member.email}</span>
                </a>
              ) : null}
              {member.phone ? (
                <a href={`tel:${member.phone}`} className="flex items-center gap-3 text-brand-dark hover:text-brand-primary">
                  <span className="w-9 h-9 rounded-full bg-brand-navy/5 text-brand-navy inline-flex items-center justify-center flex-shrink-0"><Phone size={15} strokeWidth={1.5} /></span>
                  <span className="text-sm">{member.phone}</span>
                </a>
              ) : null}
            </div>
          ) : null}
          {/* Socials */}
          {socials.length ? (
            <div className={`${member.email || member.phone ? "mt-6" : "mt-8 pt-6 border-t border-black/10"} flex flex-wrap gap-2`} data-testid="member-modal-socials">
              {socials.map((s) => (
                <a
                  key={s.kind}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-black/10 hover:border-brand-navy hover:bg-brand-navy hover:text-white text-brand-navy transition-colors"
                  aria-label={s.label}
                  data-testid={`member-social-${s.kind}`}
                >
                  <SocialIcon kind={s.kind} />
                  <span className="text-xs tracking-[0.16em] uppercase">{s.label}</span>
                </a>
              ))}
            </div>
          ) : null}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GovernancePage;
